import { DatePipe } from '@angular/common';
import { AppConstants } from 'src/app/settings/app-constants';

export function transformDateToApiFormat(value: any): string {
    if (value) {
        const datePipe = new DatePipe('en-ZA');
        return value instanceof Date
            ? datePipe.transform(value, AppConstants.API_DATE_FORMAT)
            : value;
    }
}

export function getFirstDayOfCurrentWeek(): Date {
    const today = new Date();
    const diff = today.getDate() - today.getDay();
    return new Date(today.setDate(diff));
}

export function getLastDayOfCurrentWeek(): Date {
    const today = new Date();
    const diff = today.getDate() - today.getDay() + 6;
    return new Date(today.setDate(diff));
}
export class DateUtil {
    static getRoundedDayDifference(startDate: Date, endDate: Date, changeZeroToOne?: boolean) {
        if (!(startDate instanceof Date)) {
            startDate = new Date(startDate);
        }
        if (!(endDate instanceof Date)) {
            endDate = new Date(endDate);
        }
        const oneDay = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
        const dayDiff = (endDate.getTime() - startDate.getTime()) / oneDay;
        const diffInDays = Math.round(dayDiff);
        if (diffInDays == 0 && changeZeroToOne) {
            return dayDiff < 0 ? -1 : 1;
        }
        return diffInDays;
    }

    static addDays(date, days): Date {
        if (!date) {
            date = new Date();
        }
        var result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    static addMonths(date, monthsToAdd) {
        date = new Date(date); // Create a new Date object to avoid modifying the original date
        const newMonth = date.getMonth() + monthsToAdd;
        date.setMonth(newMonth);
        // Handle potential overflow
        if (date.getMonth() !== newMonth % 12) {
            date.setDate(0); // Move to the last day of the previous month
        }
        return date;
    }

    static getCurrentWeekDates(): { startDate: Date; endDate: Date } {
        const currentDate = new Date();
        const currentDay = currentDate.getDay(); // 0 (Sunday) to 6 (Saturday)
        // Calculate the start date of the current week
        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - currentDay);
        // Calculate the end date of the current week
        const endDate = new Date(currentDate);
        endDate.setDate(startDate.getDate() + 6); // Adding 6 days to get the end of the week
        return {
            startDate: startDate,
            endDate: endDate,
        };
    }

    static getLastWeekDates(): { startDate: Date; endDate: Date } {
        const currentDate = new Date();
        const currentDay = currentDate.getDay(); // 0 (Sunday) to 6 (Saturday)
        // Calculate the start date of the last week
        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - currentDay - 7);
        // Calculate the end date of the last week
        const endDate = new Date(currentDate);
        endDate.setDate(startDate.getDate() + 6); // Adding 6 days to get the end of the week
        return {
            startDate: startDate,
            endDate: endDate,
        };
    }

    static getMonthDates(currentDate?: Date): { startDate: Date; endDate: Date } {
        currentDate = currentDate || new Date();
        // Calculate the start date of the current month
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        // Calculate the end date of the current month
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        return {
            startDate: startDate,
            endDate: endDate,
        };
    }

    static getYearDates(currentDate?: Date): { startDate: Date; endDate: Date } {
        let year = (currentDate || new Date()).getFullYear();
        // Calculate the start date of the current year
        const startDate = new Date(year, 0, 1);
        // Calculate the end date of the current year
        const endDate = new Date(year, 11, 31);
        return {
            startDate: startDate,
            endDate: endDate,
        };
    }

    static getDayStartAndEndDates(date?: Date): {
        startDate: Date;
        endDate: Date;
    } {
        const clonedDate = new Date(date || new Date());
        clonedDate.setHours(0, 0, 0, 0);
        const startDate = new Date(clonedDate);
        clonedDate.setHours(23, 59, 59, 999);
        const endDate = new Date(clonedDate);
        return {
            startDate: startDate,
            endDate: endDate,
        };
    }

    static getPresetRanges() {
        let tDates = this.getDayStartAndEndDates();
        let yesDate = this.addDays(new Date(), -1);
        let yeDates = this.getDayStartAndEndDates(yesDate);
        let wDates = this.getCurrentWeekDates();
        let lwDates = this.getLastWeekDates();
        let mDates = this.getMonthDates();
        let yDates = this.getYearDates();
        let lmDates = this.getMonthDates(new Date(new Date().setMonth(new Date().getMonth() - 1)));
        let lyDates = this.getYearDates(new Date(new Date().setDate(new Date().getDate() - 365)));
        let fqDates = this.getQuarterDates(1);
        let sqDates = this.getQuarterDates(2);
        let tqDates = this.getQuarterDates(3);
        let foqDates = this.getQuarterDates(4);
        return {
            todayStart: tDates.startDate,
            todayEnd: tDates.endDate,
            yestardayStart: yeDates.startDate,
            yestardayEnd: yeDates.endDate,
            weekStart: wDates.startDate,
            weekEnd: wDates.endDate,
            lastWeekStart: lwDates.startDate,
            lastWeekEnd: lwDates.endDate,
            monthStart: mDates.startDate,
            monthEnd: mDates.endDate,
            lastMonthStart: lmDates.startDate,
            lastMonthEnd: lmDates.endDate,
            yearStart: yDates.startDate,
            yearEnd: yDates.endDate,
            lastYearStart: lyDates.startDate,
            lastYearEnd: lyDates.endDate,
            firstQuarterStart: fqDates.startDate,
            firstQuarterEnd: fqDates.endDate,
            secondQuarterStart: sqDates.startDate,
            secondQuarterEnd: sqDates.endDate,
            thirdQuarterStart: tqDates.startDate,
            thirdQuarterEnd: tqDates.endDate,
            fourthQuarterStart: foqDates.startDate,
            fourthQuarterEnd: foqDates.endDate,
        };
    }

    static getDate(dateString: string): Date {
        const date = new Date(dateString);
        const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return newDate;
    }

    static getOnlyDate(date: Date): Date {
        const inputDate = new Date(date);
        const newDate = new Date(
            inputDate.getFullYear(),
            inputDate.getMonth(),
            inputDate.getDate(),
        );
        return newDate;
    }

    static dateEqual(date1: Date, date2: Date) {
        const firsttDate = new Date(date1);
        const secondtDate = new Date(date2);

        return (
            firsttDate.getFullYear() == secondtDate.getFullYear() &&
            firsttDate.getMonth() == secondtDate.getMonth() &&
            firsttDate.getDate() == secondtDate.getDate()
        );
    }

    static getOnlyDateString(dateToParsed: Date) {
        let date: Date = new Date(dateToParsed);
        let year: number = date.getFullYear();
        let month: number = date.getMonth() + 1; // Months are zero-based
        let day: number = date.getDate();

        let dateString: string = `${year}-${month}-${day}`;
        return dateString;
    }
    static getQuarterDates(quarter: number): { startDate: Date; endDate: Date } {
        const currentYear = new Date().getFullYear();
        let startDate: Date;
        let endDate: Date;
        switch (quarter) {
            case 1:
                startDate = new Date(currentYear, 0, 1);
                endDate = new Date(currentYear, 2, 31);
                break;
            case 2:
                startDate = new Date(currentYear, 3, 1);
                endDate = new Date(currentYear, 5, 30);
                break;
            case 3:
                startDate = new Date(currentYear, 6, 1);
                endDate = new Date(currentYear, 8, 30);
                break;
            case 4:
                startDate = new Date(currentYear, 9, 1);
                endDate = new Date(currentYear, 11, 31);
                break;
            default:
                throw new Error('Invalid quarter');
        }
        return { startDate, endDate };
    }
}

export class GuidUtil {
    static newGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) || 0,
                v = c == 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    static generateRandomString(length: number = 8) {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let randomString = '';
        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charset.length);
            randomString += charset.charAt(randomIndex);
        }
        return randomString;
    }
}
